import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import type { ChecklistSnapshotAtomicCreateInput } from '@/adapters/IDatabaseAdapter';
import { CHECKLIST_SNAPSHOTS_FEATURE } from '@/services/sync/SyncCanaryFeatureService';

export { CHECKLIST_SNAPSHOTS_FEATURE };

export interface ChecklistCreationTemplate {
  id: string;
  versao?: number | null;
}

export interface ChecklistSnapshotCreationInput {
  companyId: string;
  userId: string;
  actorUserId?: string;
  clientBuild: number;
  template: ChecklistCreationTemplate;
}

export interface ChecklistCreationRecord {
  id: string;
  template_id: string;
  empresa_id: string;
  usuario_id: string;
  status: 'in_progress';
  snapshot_id: string | null;
  data_criacao: string;
  sequencial_numero: null;
  sequencial_codigo: null;
  versao: number;
  created_at: string;
  updated_at: string;
}

export interface ChecklistSnapshotRecord {
  id: string;
  empresa_id: string;
  checklist_id: string;
  template_id: string;
  template_schema_version_id: string;
  schema_payload: string;
  schema_hash: string;
  source: 'app';
  created_by: string;
  created_at: number;
}

export interface ChecklistTemplateSchemaVersionRecord {
  id: string;
  empresa_id: string;
  template_id: string;
  version: number;
  schema_payload: string;
  schema_hash: string;
  source: string;
  created_by: string | null;
  created_at: number;
}

export interface ChecklistSnapshotFieldRecord {
  id: string;
  empresa_id: string;
  snapshot_id: string;
  original_campo_id: string;
  field_key: string;
  grupo: string | null;
  nome: string;
  tipo: string;
  ordem: number;
  obrigatorio: number;
  visivel: number;
  config: string;
  created_at: number;
}

export interface ChecklistSnapshotCreationDatabase {
  listItems<T>(collection: string, query?: Record<string, unknown>): Promise<T[]>;
  createItem<T>(collection: string, item: T, skipPendingOperation?: boolean): Promise<string>;
  createChecklistSnapshotAndPending(input: ChecklistSnapshotAtomicCreateInput): Promise<void>;
}

export interface ChecklistSnapshotCreationTables {
  checklists: string;
  templateFields: string;
  templateSchemaVersions: string;
  snapshots: string;
  snapshotFields: string;
}

export interface ChecklistSnapshotFeatureProvider {
  isEnabled(input: {
    companyId: string;
    clientBuild: number;
    featureKey: string;
  }): Promise<boolean | null> | boolean | null;
}

export interface ChecklistSnapshotCreationDependencies {
  database: ChecklistSnapshotCreationDatabase;
  featureProvider: ChecklistSnapshotFeatureProvider;
  tables: ChecklistSnapshotCreationTables;
  createId: () => string;
  now: () => Date;
}

export interface ChecklistSnapshotCreationResult {
  checklist: ChecklistCreationRecord;
  snapshot: ChecklistSnapshotRecord | null;
  snapshotFields: ChecklistSnapshotFieldRecord[];
  usedSnapshot: boolean;
}

export type TemplateField = Record<string, unknown> & {
  id: string;
  empresa_id: string;
  template_id: string;
  nome: string;
  tipo: string;
  grupo?: string | null;
  ordem?: number | null;
  obrigatorio?: boolean | number | null;
  visivel?: boolean | number | null;
  retired_at?: string | number | null;
};

type TemplateSchemaVersion = {
  id: string;
  empresa_id: string;
  template_id: string;
  version: number;
  schema_payload?: string | Record<string, unknown> | null;
  schema_hash?: string | null;
  source?: string | null;
  created_by?: string | null;
  created_at?: number | string | null;
};

export class ChecklistSnapshotCreationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChecklistSnapshotCreationError';
  }
}

const asBooleanInteger = (value: unknown, fallback: number): number => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return value === 0 ? 0 : 1;
  return value === '0' || value === 'false' ? 0 : 1;
};

export const stableStringify = (value: unknown): string => {
  const normalize = (current: unknown): unknown => {
    if (Array.isArray(current)) return current.map(normalize);
    if (!current || typeof current !== 'object') return current;
    return Object.keys(current as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        result[key] = normalize((current as Record<string, unknown>)[key]);
        return result;
      }, {});
  };

  return JSON.stringify(normalize(value));
};

const sha512Hex = (value: string): string => Array.from(nacl.hash(naclUtil.decodeUTF8(value)))
  .map(byte => byte.toString(16).padStart(2, '0'))
  .join('');

const PROVISIONAL_SCHEMA_HASH_PREFIX = 'local-provisional-sha512:';

const configFromTemplateField = (field: TemplateField): Record<string, unknown> => {
  const {
    id: _id,
    empresa_id: _empresaId,
    template_id: _templateId,
    versao: _version,
    grupo: _group,
    nome: _name,
    tipo: _type,
    ordem: _order,
    obrigatorio: _required,
    visivel: _visible,
    retired_at: _retiredAt,
    retired_by: _retiredBy,
    created_at: _createdAt,
    updated_at: _updatedAt,
    ...config
  } = field;
  if (config.deletavel !== undefined && config.deletavel !== null) {
    config.deletavel = asBooleanInteger(config.deletavel, 1) === 1;
  }
  if (config.alteravel !== undefined && config.alteravel !== null) {
    config.alteravel = asBooleanInteger(config.alteravel, 1) === 1;
  }
  // Assistência de preenchimento não muda o contrato histórico nem seu hash.
  delete config.endereco_gps;
  return config;
};

export const buildTemplateSchemaPayload = (
  templateId: string,
  templateVersion: number,
  activeFields: TemplateField[]
): Record<string, unknown> => ({
  template_id: templateId,
  template_version: templateVersion,
  fields: activeFields.map((field, index) => ({
    field_key: `field:${field.id}`,
    original_campo_id: field.id,
    grupo: typeof field.grupo === 'string' ? field.grupo : null,
    nome: field.nome,
    tipo: field.tipo,
    ordem: Number(field.ordem ?? index),
    obrigatorio: asBooleanInteger(field.obrigatorio, 0) === 1,
    visivel: asBooleanInteger(field.visivel, 1) === 1,
    config: configFromTemplateField(field),
  })),
});

export const hashTemplateSchemaPayload = (payload: Record<string, unknown>): string => (
  `${PROVISIONAL_SCHEMA_HASH_PREFIX}${sha512Hex(stableStringify(payload))}`
);

const assertIdentifier = (value: string, label: string): string => {
  const normalized = value.trim();
  if (!normalized) throw new ChecklistSnapshotCreationError(`${label} ausente para criar checklist.`);
  return normalized;
};

export class ChecklistSnapshotCreationService {
  constructor(private readonly dependencies: ChecklistSnapshotCreationDependencies) {}

  async create(input: ChecklistSnapshotCreationInput): Promise<ChecklistSnapshotCreationResult> {
    const companyId = assertIdentifier(input.companyId, 'Empresa');
    const userId = assertIdentifier(input.userId, 'Usuario');
    const actorUserId = assertIdentifier(input.actorUserId ?? userId, 'Ator');
    const templateId = assertIdentifier(input.template?.id ?? '', 'Template');
    const templateVersion = Number(input.template?.versao ?? 1);
    if (!Number.isInteger(templateVersion) || templateVersion <= 0 || input.clientBuild <= 0) {
      throw new ChecklistSnapshotCreationError('Versao do template ou build invalido para criar checklist.');
    }

    const snapshotEnabled = await this.isSnapshotEnabled(companyId, input.clientBuild);
    const now = this.dependencies.now();
    const checklist: ChecklistCreationRecord = {
      id: this.dependencies.createId(),
      template_id: templateId,
      empresa_id: companyId,
      usuario_id: userId,
      status: 'in_progress',
      snapshot_id: null,
      data_criacao: now.toISOString(),
      sequencial_numero: null,
      sequencial_codigo: null,
      versao: templateVersion,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };

    if (!snapshotEnabled) {
      await this.dependencies.database.createItem(this.dependencies.tables.checklists, checklist, false);
      return { checklist, snapshot: null, snapshotFields: [], usedSnapshot: false };
    }

    const materialized = await this.materializeSnapshot({
      checklist,
      companyId,
      actorUserId,
      templateId,
      templateVersion,
      createdAt: now.getTime(),
    });

    checklist.snapshot_id = materialized.snapshot.id;

    return this.persistAtomically(
      checklist,
      materialized.snapshot,
      materialized.snapshotFields,
      materialized.schemaVersion
    );
  }

  private async isSnapshotEnabled(companyId: string, clientBuild: number): Promise<boolean> {
    const decision = await this.dependencies.featureProvider.isEnabled({
      companyId,
      clientBuild,
      featureKey: CHECKLIST_SNAPSHOTS_FEATURE,
    });
    if (decision === null) {
      throw new ChecklistSnapshotCreationError(
        'Decisao estrutural do checklist ainda nao foi sincronizada; checklist nao foi criado.'
      );
    }
    return decision;
  }

  private async materializeSnapshot(input: {
    checklist: ChecklistCreationRecord;
    companyId: string;
    actorUserId: string;
    templateId: string;
    templateVersion: number;
    createdAt: number;
  }): Promise<{
    schemaVersion?: ChecklistTemplateSchemaVersionRecord;
    snapshot: ChecklistSnapshotRecord;
    snapshotFields: ChecklistSnapshotFieldRecord[];
  }> {
    const [schemaVersions, templateFields] = await Promise.all([
      this.dependencies.database.listItems<TemplateSchemaVersion>(this.dependencies.tables.templateSchemaVersions, {
        empresa_id: input.companyId,
        template_id: input.templateId,
        version: input.templateVersion,
      }),
      this.dependencies.database.listItems<TemplateField>(this.dependencies.tables.templateFields, {
        empresa_id: input.companyId,
        template_id: input.templateId,
      }),
    ]);

    const matchingVersions = schemaVersions.filter(version => (
      version.empresa_id === input.companyId
      && version.template_id === input.templateId
      && Number(version.version) === input.templateVersion
    ));

    const activeFields = templateFields
      .filter(field => field.empresa_id === input.companyId && field.template_id === input.templateId)
      .filter(field => field.retired_at === null || field.retired_at === undefined)
      .sort((left, right) => Number(left.ordem ?? 0) - Number(right.ordem ?? 0) || left.id.localeCompare(right.id));
    if (activeFields.length === 0) {
      throw new ChecklistSnapshotCreationError('Template sem campos ativos; checklist nao foi criado.');
    }

    const expectedSchemaPayload = buildTemplateSchemaPayload(
      input.templateId,
      input.templateVersion,
      activeFields
    );
    const schemaMatches = (version: TemplateSchemaVersion): boolean => {
      if (version.schema_payload === undefined || version.schema_payload === null) return false;

      let localSchemaPayload: unknown = version.schema_payload;
      if (typeof localSchemaPayload === 'string') {
        try {
          localSchemaPayload = JSON.parse(localSchemaPayload);
        } catch {
          return false;
        }
      }
      return stableStringify(localSchemaPayload) === stableStringify(expectedSchemaPayload);
    };

    const exactVersions = matchingVersions.filter(schemaMatches);
    const divergentVersion = matchingVersions.find(version => !schemaMatches(version));
    if (divergentVersion && exactVersions.length === 0) {
      throw new ChecklistSnapshotCreationError(
        'Catalogo de schema do template esta desatualizado; checklist nao foi criado.'
      );
    }

    // A escolha usa apenas identidade e origem, nunca created_at ou o relogio local.
    const localSchemaVersion = [...exactVersions].sort((left, right) => {
      const leftIsProvisional = left.source === 'provisional' ? 1 : 0;
      const rightIsProvisional = right.source === 'provisional' ? 1 : 0;
      return leftIsProvisional - rightIsProvisional || left.id.localeCompare(right.id);
    })[0];
    const schemaVersion = localSchemaVersion
      ? undefined
      : {
          id: this.dependencies.createId(),
          empresa_id: input.companyId,
          template_id: input.templateId,
          version: input.templateVersion,
          schema_payload: stableStringify(expectedSchemaPayload),
          schema_hash: hashTemplateSchemaPayload(expectedSchemaPayload),
          source: 'provisional',
          created_by: input.actorUserId,
          created_at: input.createdAt,
        } satisfies ChecklistTemplateSchemaVersionRecord;
    const schemaVersionId = localSchemaVersion?.id ?? schemaVersion?.id;
    if (!schemaVersionId) {
      throw new ChecklistSnapshotCreationError(
        'Catalogo de schema do template sem identidade; checklist nao foi criado.'
      );
    }
    const snapshotSchemaHash = localSchemaVersion?.schema_hash ?? schemaVersion?.schema_hash;
    if (!snapshotSchemaHash) {
      throw new ChecklistSnapshotCreationError(
        'Catalogo de schema do template sem hash; checklist nao foi criado.'
      );
    }

    const snapshotId = this.dependencies.createId();
    const snapshotFields = activeFields.map((field, index): ChecklistSnapshotFieldRecord => ({
      id: this.dependencies.createId(),
      empresa_id: input.companyId,
      snapshot_id: snapshotId,
      original_campo_id: field.id,
      field_key: `field:${field.id}`,
      grupo: typeof field.grupo === 'string' ? field.grupo : null,
      nome: field.nome,
      tipo: field.tipo,
      ordem: Number(field.ordem ?? index),
      obrigatorio: asBooleanInteger(field.obrigatorio, 0),
      visivel: asBooleanInteger(field.visivel, 1),
      config: stableStringify(configFromTemplateField(field)),
      created_at: input.createdAt,
    }));
    const snapshot: ChecklistSnapshotRecord = {
      id: snapshotId,
      empresa_id: input.companyId,
      checklist_id: input.checklist.id,
      template_id: input.templateId,
      template_schema_version_id: schemaVersionId,
      schema_payload: stableStringify(expectedSchemaPayload),
      schema_hash: snapshotSchemaHash,
      source: 'app',
      created_by: input.actorUserId,
      created_at: input.createdAt,
    };
    return { schemaVersion, snapshot, snapshotFields };
  }

  private async persistAtomically(
    checklist: ChecklistCreationRecord,
    snapshot: ChecklistSnapshotRecord,
    snapshotFields: ChecklistSnapshotFieldRecord[],
    schemaVersion?: ChecklistTemplateSchemaVersionRecord
  ): Promise<ChecklistSnapshotCreationResult> {
    try {
      await this.dependencies.database.createChecklistSnapshotAndPending({
        checklist: {
          collection: this.dependencies.tables.checklists,
          record: checklist as unknown as Record<string, unknown>,
        },
        ...(schemaVersion
          ? {
              schemaVersion: {
                collection: this.dependencies.tables.templateSchemaVersions,
                record: schemaVersion as unknown as Record<string, unknown>,
              },
            }
          : {}),
        snapshot: {
          collection: this.dependencies.tables.snapshots,
          record: snapshot as unknown as Record<string, unknown>,
        },
        snapshotFields: {
          collection: this.dependencies.tables.snapshotFields,
          records: snapshotFields as unknown as Record<string, unknown>[],
        },
        pendingOperation: {
          id: this.dependencies.createId(),
          collection: this.dependencies.tables.checklists,
          itemId: checklist.id,
          data: JSON.stringify(checklist),
          timestamp: this.dependencies.now().getTime(),
        },
      });
      return { checklist, snapshot, snapshotFields, usedSnapshot: true };
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      throw new ChecklistSnapshotCreationError(
        `Falha na transacao local do checklist com snapshot; nenhuma alteracao parcial foi aceita: ${detail}`
      );
    }
  }
}
